import { memo, type ReactNode, useMemo } from 'react';

import { cn } from '@air/utils-tailwind';

import { AssetModalHeaderSeparator } from '~/components/AssetModalV2/Header/components/AssetModalHeaderSeparator';
import { CloseButton } from '~/components/AssetModalV2/Header/components/CloseButton';
import { useFullscreenContext } from '~/components/AssetModalV2/shared/context/FullscreenContext';
import { type FileExtensionName, fileExtensionStylingMap, genericFile } from '~/components/FileExtensionPreview/types';
import { centralizedClipSelector } from '~/store/centralizedClip/selectors';
import { useAirStore } from '~/utils/ReduxUtils';

const isFileExtensionName = (extension: string): extension is FileExtensionName => extension in fileExtensionStylingMap;

export type FullscreenHeaderProps = {
  centerAccessory?: ReactNode;
  navigationArrows: ReactNode;
};

export const FullscreenHeader = memo(({ centerAccessory, navigationArrows }: FullscreenHeaderProps) => {
  const store = useAirStore();
  const clip = centralizedClipSelector(store.getState());
  const { toggleFullscreen } = useFullscreenContext();
  const { iconComponent: IconComponent } = useMemo(() => {
    const normalizedExt = clip?.ext?.toLowerCase() || '';

    return isFileExtensionName(normalizedExt) ? fileExtensionStylingMap[normalizedExt] : genericFile;
  }, [clip?.ext]);

  return (
    <div
      className={cn(
        'pointer-events-auto relative w-full items-center gap-2 rounded-8 ads:bg-glass/85 bg-gray-1/85 px-5 py-3 ads:text-primary text-gray-12 backdrop-blur-[18px]',
        centerAccessory ? 'grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]' : 'flex justify-between',
      )}
    >
      <div className={cn('flex min-w-0 items-center gap-2', !centerAccessory && 'flex-1')}>
        <IconComponent className="size-4 shrink-0 text-current" />
        <span className="truncate">{clip.title ?? clip.importedName ?? 'Untitled'}</span>
      </div>
      {centerAccessory ? <div className="pointer-events-auto justify-self-center">{centerAccessory}</div> : null}
      <div className={cn('flex shrink-0 items-center justify-end gap-4', centerAccessory && 'justify-self-end')}>
        {navigationArrows}
        <AssetModalHeaderSeparator />
        <CloseButton onClose={toggleFullscreen} />
      </div>
    </div>
  );
});

FullscreenHeader.displayName = 'FullscreenHeader';
